import type { GridConfig } from '@shared/types';

export type GridParseResult = {
  type: 'grid';
  value: string[];
  gridConfig: GridConfig;
};

/**
 * Detects grid syntax: `// grid: CxR [opt1, opt2, ...]`
 *
 * The comment must start with "grid:" followed by dimensions and an option
 * list. Dimensions may be literal integers or $-references. The default value
 * can be any 2D array literal or even an empty string — we always reconstruct
 * from gridConfig.
 *
 * Returns null if the comment does not match the grid pattern.
 */
export function parseGridType(
  valueStr: string,
  comment: string,
): GridParseResult | null {
  const gridMatch = comment.match(
    /^grid:\s*(\d+|\$[a-zA-Z_][a-zA-Z0-9_]*)\s*[xX]\s*(\d+|\$[a-zA-Z_][a-zA-Z0-9_]*)\s*\[([^\]]+)\]/i,
  );
  if (!gridMatch) return null;

  // Dimension tokens: either a literal integer or a $-reference string
  const colsRaw = gridMatch[1];
  const rowsRaw = gridMatch[2];
  const cols: number | string = /^\d+$/.test(colsRaw)
    ? parseInt(colsRaw, 10)
    : colsRaw;
  const rows: number | string = /^\d+$/.test(rowsRaw)
    ? parseInt(rowsRaw, 10)
    : rowsRaw;
  // For initial parsing we need concrete numbers; use 0 as fallback for
  // unresolved references — the resolved value is applied at render time.
  const colsNum = typeof cols === 'number' ? cols : 0;
  const rowsNum = typeof rows === 'number' ? rows : 0;

  // Parse option tokens — each may carry optional #hex colour and/or :WxH
  // span suffixes, e.g. "pot#ff3300:2x1" → name "pot", colour "#ff3300",
  // span [2, 1]. Suffix order is always: name[#color][:WxH].
  const rawTokens = gridMatch[3]
    .split(',')
    .map((s) => s.trim().replace(/^["']|["']$/g, ''))
    .filter((s) => s.length > 0);
  const rawOpts: string[] = [];
  const optionColours: Record<string, string> = {};
  const optionSpans: Record<string, [number, number]> = {};

  for (const token of rawTokens) {
    // Strip optional :WxH span suffix first (must come last in the token)
    const spanMatch = token.match(/^(.*?)(?::(\d+)[xX](\d+))?$/);
    const tokenNoSpan = spanMatch?.[1] ?? token;
    const spanW = spanMatch?.[2] ? parseInt(spanMatch[2], 10) : 1;
    const spanH = spanMatch?.[3] ? parseInt(spanMatch[3], 10) : 1;

    const hashIdx = tokenNoSpan.indexOf('#');
    let name: string;
    if (
      hashIdx > 0 &&
      /^#[0-9a-fA-F]{3,8}$/.test(tokenNoSpan.slice(hashIdx))
    ) {
      name = tokenNoSpan.slice(0, hashIdx);
      optionColours[name] = tokenNoSpan.slice(hashIdx);
    } else {
      name = tokenNoSpan;
    }
    rawOpts.push(name);
    if (spanW > 1 || spanH > 1) {
      optionSpans[name] = [spanW, spanH];
    }
  }

  const hasCustomColours = Object.keys(optionColours).length > 0;
  const hasCustomSpans = Object.keys(optionSpans).length > 0;

  if (
    !(
      (colsNum > 0 || typeof cols === 'string') &&
      (rowsNum > 0 || typeof rows === 'string') &&
      rawOpts.length > 0
    )
  ) {
    return null;
  }

  // Try to parse the default 2D array literal into a flat row-major string[].
  // Accept [[...],[...]] or [] or "".
  const flatDefault: string[] = [];

  const outerMatch = valueStr.match(
    /^\[\s*((?:\[.*?\](?:\s*,\s*)?)*)\s*\]$/s,
  );
  if (outerMatch) {
    const innerRows = outerMatch[1].match(/\[([^\]]*)\]/g) ?? [];
    for (const innerRow of innerRows) {
      const cells = innerRow
        .slice(1, -1)
        .split(',')
        .map((s) => s.trim().replace(/^["']|["']$/g, ''));
      flatDefault.push(...cells);
    }
  }

  // Pad / truncate to exactly cols*rows when dimensions are known.
  // When one or both dimensions are $-references (colsNum/rowsNum = 0),
  // keep the values parsed from the literal as-is and let GridInput
  // pad at render time once the references are resolved.
  const defaultFilled = colsNum * rowsNum;
  if (defaultFilled > 0) {
    while (flatDefault.length < defaultFilled) flatDefault.push(rawOpts[0]);
    flatDefault.length = defaultFilled;
  }

  // Validate all cells: "span" sentinel is always preserved as-is;
  // values in the options list pass through; anything else is replaced
  // with the first option.
  const validatedFlat = flatDefault.map((cell) =>
    cell === 'span' || rawOpts.includes(cell) ? cell : rawOpts[0],
  );

  return {
    type: 'grid',
    value: validatedFlat,
    gridConfig: {
      cols,
      rows,
      options: rawOpts,
      ...(hasCustomColours ? { optionColours } : {}),
      ...(hasCustomSpans ? { optionSpans } : {}),
    },
  };
}
